<template>
  <div class="security-guards">
    <h2 class="subheading grey--text">{{ $t('security_manage_guards') }}</h2>
    <v-container class="my-5">
      <v-layout align-center justify-center fill-height>
          <v-btn color="primary" dark @click="showCreateGuardDialog = true">
            + {{ $t('security_create_new_guard') }}
          </v-btn>
      </v-layout>

      <v-layout class="my-4" align-center justify-center fill-height>
        <v-flex>
          <v-card-title>
            {{ $t('security_guards_list') }}
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="$t('Buscar')"
              single-line
              hide-details
              class="mr-3"
            ></v-text-field>
            <v-btn color="error" dark small @click="downloadPDF" :disabled="guards.length === 0">
              <v-icon left small>picture_as_pdf</v-icon>
              Generar PDF
            </v-btn>
          </v-card-title>

          <v-alert v-if="securityError" type="error" dismissible class="mb-3">
            {{ securityError }}
          </v-alert>

          <v-data-table
            :headers="guardHeaders"
            :items="guards"
            :search="search"
            :loading="isLoading"
            :rows-per-page-text="$t('filas_por_pagina')"
            :rows-per-page-items="[
              {text: '5', value: 5},
              {text: '10', value: 10},
              {text: '25', value: 25},
              {text: this.$t('todos'), value: -1}
            ]"
            class="elevation-1"
          >
            <template slot="items" slot-scope="props">
              <td class="justify-center layout px-0">
                <v-icon medium class="mr-2" color="primary" @click="viewGuardDetail(props.item)">visibility</v-icon>
                <v-icon medium class="mr-2" color="blue" @click="openEditDialog(props.item)">edit</v-icon>
                <v-icon medium class="mr-2" color="warning" @click="openAssignGuardDialog(props.item)">assignment_ind</v-icon>
                <v-icon medium color="red" @click="openDeleteDialog(props.item)">delete</v-icon>
              </td>
              <td>{{ props.item.first_name }} {{ props.item.last_name }}</td>
              <td>{{ props.item.username || 'N/A' }}</td>
              <td>{{ props.item.badge_number || 'N/A' }}</td>
              <td>{{ props.item.assigned_company_name || '-' }}</td>
              <td>{{ props.item.is_active ? $t('Si') : 'No' }}</td>
            </template>
            <template v-slot:no-results>
              <v-alert :value="true" color="error" icon="warning">
                {{$t('ningun_resultado_1')}} "{{ search }}" {{$t('ningun_resultado_2')}}
              </v-alert>
            </template>
            <template v-slot:pageText="props">
              {{props.rowsPerPage}}
              {{$t('security_guards_list')}} {{ props.pageStart }} - {{ props.pageStop }} {{$t('prep_de')}} {{ props.itemsLength }}
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- DIALOG: Crear/Editar Guardia -->
    <v-dialog v-model="showCreateGuardDialog" max-width="500px" persistent>
      <v-form ref="createGuardForm" @submit.prevent="isEditing ? saveEditGuard() : createGuard()">
        <v-card class="elevation-5">
          <v-toolbar dark color="primary">
            <v-toolbar-title>{{ isEditing ? $t('modificar') : $t('security_create_new_guard') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon color="white" flat @click="closeCreateDialog" class="align-self-end">
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-tabs v-model="createGuardTab" grow>
            <v-tab>{{ $t('general_information') || 'Informacion general' }}</v-tab>
            <v-tab>{{ $t('documentation') || 'Documentacion' }}</v-tab>

            <v-tab-item>
              <v-card-text class="pt-4">
                 <v-layout row wrap justify-center>
                   <v-flex xs12 class="text-xs-center mb-4">
                     <v-avatar size="100" color="grey lighten-2" class="elevation-2" style="cursor: pointer;" @click="openAvatarPicker()">
                       <img v-if="newGuard.avatarPreview" :src="newGuard.avatarPreview" alt="Avatar">
                       <v-icon v-else size="64" color="white">person</v-icon>
                     </v-avatar>
                     <div class="mt-3">
                       <v-btn small color="primary" @click="openAvatarPicker()" outline round>
                         <v-icon left>camera_alt</v-icon> {{$t('subir_imagen')}}
                       </v-btn>
                     </div>
                     <input type="file" ref="avatarInput" style="display: none;" accept="image/jpeg, image/png" @change="onAvatarFilePicked" />
                   </v-flex>

                   <v-flex xs12 sm6 class="px-2">
                     <v-text-field
                       v-model="newGuard.firstName"
                       :label="$t('Nombre')"
                       prepend-icon="person"
                       required
                     ></v-text-field>
                   </v-flex>

                   <v-flex xs12 sm6 class="px-2">
                     <v-text-field
                       v-model="newGuard.lastName"
                       :label="$t('Apellido')"
                       prepend-icon="person_outline"
                       required
                     ></v-text-field>
                   </v-flex>

                   <v-flex xs12 sm6 class="px-2 mt-2">
                     <v-text-field
                       v-model="newGuard.password"
                       :label="$t('password')"
                       prepend-icon="lock"
                       type="password"
                       :required="!isEditing"
                       :hint="isEditing ? 'Deja en blanco para no cambiar' : $t('password_min_length')"
                     ></v-text-field>
                   </v-flex>

                   <v-flex xs12 sm6 class="px-2 mt-2" v-if="isEditing">
                     <v-switch v-model="newGuard.isActive" :label="$t('Activo')"></v-switch>
                   </v-flex>
                 </v-layout>
              </v-card-text>
            </v-tab-item>

            <v-tab-item>
              <v-card-text class="pt-4">
                <v-layout row wrap>
                  <v-flex xs12 class="mb-3">
                    <v-text-field
                      :value="newGuard.ineFrontName"
                      label="INE frente"
                      prepend-icon="badge"
                      readonly
                      placeholder="Selecciona una imagen"
                    ></v-text-field>
                    <v-btn small color="primary" outline round @click="$refs.ineFrontInput.click()">
                      <v-icon left>attach_file</v-icon>
                      Subir INE frente
                    </v-btn>
                    <input
                      type="file"
                      ref="ineFrontInput"
                      style="display: none;"
                      accept="image/jpeg, image/png"
                      @change="onDocumentFilePicked('ineFrontFile', 'ineFrontName', $event)"
                    />
                  </v-flex>

                  <v-flex xs12 class="mb-3">
                    <v-text-field
                      :value="newGuard.ineBackName"
                      label="INE reverso"
                      prepend-icon="badge"
                      readonly
                      placeholder="Selecciona una imagen"
                    ></v-text-field>
                    <v-btn small color="primary" outline round @click="$refs.ineBackInput.click()">
                      <v-icon left>attach_file</v-icon>
                      Subir INE reverso
                    </v-btn>
                    <input
                      type="file"
                      ref="ineBackInput"
                      style="display: none;"
                      accept="image/jpeg, image/png"
                      @change="onDocumentFilePicked('ineBackFile', 'ineBackName', $event)"
                    />
                  </v-flex>
                  <v-flex xs12>
                    <v-text-field
                      :value="newGuard.responsibilityFormatName"
                      label="Formato de responsabilidad (PDF)"
                      prepend-icon="picture_as_pdf"
                      readonly
                      placeholder="Selecciona un archivo PDF"
                    ></v-text-field>
                    <v-btn small color="primary" outline round @click="$refs.responsibilityFormatInput.click()">
                      <v-icon left>attach_file</v-icon>
                      Subir formato de responsabilidad
                    </v-btn>
                    <input
                      type="file"
                      ref="responsibilityFormatInput"
                      style="display: none;"
                      accept="application/pdf"
                      @change="onDocumentFilePicked('responsibilityFormatFile', 'responsibilityFormatName', $event)"
                    />
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-tab-item>
          </v-tabs>

          <v-card-actions class="pb-4 px-4 justify-center">
            <v-btn color="grey darken-1" flat @click="closeCreateDialog">
               {{ $t('cancel') }}
            </v-btn>
            <v-btn 
               color="primary" 
               type="submit"
               :loading="isLoading"
               round
               class="px-4"
            >
               {{ isEditing ? $t('modificar') : $t('agregar') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- DIALOG: Asignar Guardia a Empresa A -->
    <v-dialog v-model="showAssignGuardDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title>{{ $t('security_assign_guard') }}</v-card-title>
        <v-card-text>
          <p class="mb-4">{{ selectedGuard ? selectedGuard.first_name + ' ' + selectedGuard.last_name : '' }}</p>

          <v-select
            v-model="selectedAssignClient"
            :items="assignedClients"
            item-text="name"
            item-value="id"
            :label="$t('security_select_client')"
            clearable
            outline
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="showAssignGuardDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn 
            color="primary" 
            flat 
            @click="assignGuard"
            :loading="isLoading"
          >
            {{ $t('assign') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG: Detalle de Guardia -->
    <v-dialog v-model="showGuardDetailDialog" max-width="600px">
      <v-card class="elevation-5 rounded-lg">
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ $t('security_guard_details') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" flat @click="showGuardDetailDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text v-if="guardDetail" class="pt-4 pb-4">
          <v-layout row wrap justify-center>
            <v-flex xs12 class="text-xs-center mb-4">
              <v-avatar size="100" color="primary lighten-3" class="elevation-2">
                <img v-if="guardDetail.avatar" :src="guardDetail.avatar" alt="Avatar">
                <v-icon v-else size="64" color="white">person</v-icon>
              </v-avatar>
              <div class="title mt-3 font-weight-regular">{{ guardDetail.first_name }} {{ guardDetail.last_name }}</div>
              <v-chip outline :color="guardDetail.is_active ? 'success' : 'error'" class="mt-2 font-weight-bold" small>
                 <v-icon left size="16">{{ guardDetail.is_active ? 'check_circle' : 'error' }}</v-icon>
                 {{ guardDetail.is_active ? $t('active') : $t('inactive') }}
              </v-chip>
            </v-flex>
            
            <v-flex xs12 sm6 class="px-3 mt-3 text-xs-center">
              <div class="grey--text text--darken-1 caption text-uppercase">{{ $t('username') }}</div>
              <div class="subheading">{{ guardDetail.username }}</div>
            </v-flex>

            <v-flex xs12 sm6 class="px-3 mt-3 text-xs-center">
              <div class="grey--text text--darken-1 caption text-uppercase">{{ $t('badge_number') }}</div>
              <div class="subheading">{{ guardDetail.badge_number || 'N/A' }}</div>
            </v-flex>

            <v-flex xs12 sm6 class="px-3 mt-4 text-xs-center">
              <div class="grey--text text--darken-1 caption text-uppercase">{{ $t('security_assigned_to') }}</div>
              <div class="subheading">{{ guardDetail.assigned_company_name || '-' }}</div>
            </v-flex>

            <v-flex xs12 sm6 class="px-3 mt-4 text-xs-center">
              <div class="grey--text text--darken-1 caption text-uppercase">{{ $t('created_at') }}</div>
              <div class="subheading">{{ formatDate(guardDetail.created_at) }}</div>
            </v-flex>

            <!-- Archivos del guardia -->
            <v-flex xs12 class="px-3 mt-5">
              <v-divider class="mb-4"></v-divider>
              <div class="grey--text text--darken-2 caption text-uppercase font-weight-bold mb-3">
                <v-icon small class="mr-1">attach_file</v-icon> Documentos
              </div>
              <v-layout row wrap>
                <v-flex
                  v-for="doc in guardDocuments"
                  :key="doc.key"
                  xs12 sm6
                  class="pa-2"
                >
                  <v-card flat outlined class="pa-3 d-flex align-center" style="border:1px solid #e0e0e0; border-radius:8px;">
                    <v-icon :color="doc.url ? 'primary' : 'grey lighten-1'" class="mr-3">
                      {{ doc.key === 'avatar' ? 'account_circle' : 'insert_drive_file' }}
                    </v-icon>
                    <div style="flex:1; min-width:0;">
                      <div class="caption grey--text" style="text-transform:uppercase; letter-spacing:.5px;">{{ doc.label }}</div>
                      <div v-if="doc.url" class="body-2 primary--text text-truncate">
                        <a :href="doc.url" target="_blank" rel="noopener" style="text-decoration:none; color:inherit;">
                          Ver archivo <v-icon x-small color="primary">open_in_new</v-icon>
                        </a>
                      </div>
                      <div v-else class="caption grey--text text--lighten-1">Sin archivo</div>
                    </div>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- DIALOG: Eliminar Guardia -->
    <v-dialog v-model="showDeleteGuardDialog" max-width="320">
      <v-card class="elevation-3 rounded-lg">
        <v-card-title class="headline">{{ $t('msg_eliminar_vigilante') || 'Eliminar Guardia' }}</v-card-title>
        <v-card-text>
          {{ $t('confirm_delete_guard') || '¿Estás seguro que deseas eliminar a este guardia?' }}
        </v-card-text>
        <v-card-actions class="px-3 pb-3">
          <v-btn color="grey darken-1" flat @click="showDeleteGuardDialog = false">No</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="confirmDeleteGuard" :loading="isLoading">{{ $t('Si') || 'Sí' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import {
  FETCH_MY_COMPANY,
  FETCH_GUARDS,
  CREATE_GUARD,
  UPDATE_GUARD,
  FETCH_GUARD_DETAIL,
  ASSIGN_GUARD,
  DELETE_GUARD
} from '../../../store/modules/security'
import { mapGetters } from 'vuex'

export default {
  name: 'SecurityGuards',
  data() {
    return {
      showCreateGuardDialog: false,
      showAssignGuardDialog: false,
      showGuardDetailDialog: false,
      showDeleteGuardDialog: false,
      createGuardTab: 0,
      isEditing: false,
      editingGuardId: null,
      guardToDelete: null,
      newGuard: {
        firstName: '',
        lastName: '',
        password: '',
        avatarPreview: '',
        avatarFile: null,
        isActive: true,
        ineFrontFile: null,
        ineFrontName: '',
        ineBackFile: null,
        ineBackName: '',
        responsibilityFormatFile: null,
        responsibilityFormatName: '',
        suaFile: null,
        suaName: ''
      },
      selectedGuard: null,
      selectedAssignClient: null,
      search: '',
      guardHeaders: []
    }
  },
  beforeUpdate(){
      this.guardHeaders = [
          { text: this.$t('acciones'), align: 'center', value: 'actions', sortable: false },
          { text: this.$t('Nombre'), align: 'left', value: 'first_name', sortable: true },
          { text: 'Usuario', align: 'left', value: 'username', sortable: true },
          { text: 'Placa', align: 'left', value: 'badge_number', sortable: true },
          { text: 'Asignado a', align: 'left', value: 'assigned_company_name', sortable: true },
          { text: this.$t('Activo'), align: 'left', value: 'is_active', sortable: true }
      ]
  },
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', [
      'isSecurityLoading',
      'getSecurityError',
      'getMyCompany',
      'getGuards',
      'getGuardDetail'
    ]),
    isLoading() {
      return this.isSecurityLoading
    },
    securityError() {
      return this.getSecurityError
    },
    guards() {
      return this.getGuards
    },
    guardDetail() {
      return this.getGuardDetail
    },
    guardDocuments() {
      if (!this.guardDetail) return [];
      const files = this.guardDetail.files || {};
      return [
        { key: 'avatar',              label: 'Foto / Avatar',          url: files.avatar              || this.guardDetail.avatar              || null },
        { key: 'ine_frente',          label: 'INE Frente',             url: files.ine_frente          || this.guardDetail.ine_frente          || null },
        { key: 'ine_reverso',         label: 'INE Reverso',            url: files.ine_reverso         || this.guardDetail.ine_reverso         || null },
        { key: 'sua',                 label: 'SUA',                    url: files.sua                 || this.guardDetail.sua                 || null },
        { key: 'responsibility_format', label: 'Formato Responsabilidad', url: files.responsibility_format || this.guardDetail.responsibility_format || null },
      ];
    },
    assignedClients() {
      if (this.getMyCompany && this.getMyCompany.company_contracts) {
        // Normalize contract response because backend payloads can vary by version.
        return this.getMyCompany.company_contracts
          .filter(contract => {
            if (!contract.status) return true
            return String(contract.status).toUpperCase() === 'ACTIVE'
          })
          .map(contract => ({
            id: this.getClientIdFromContract(contract),
            name: contract.company_name || contract.host_name || contract.client_company_name || contract.name || 'N/A',
            status: contract.status,
            status_display: contract.status_display,
            created_at: contract.created_at
          }))
          .filter(client => !!client.id)
      }
      if (this.getMyCompany && this.getMyCompany.contracts) {
        // Fallback for old API response format
        return this.getMyCompany.contracts
          .filter(contract => {
            if (!contract.status) return true
            return String(contract.status).toUpperCase() === 'ACTIVE'
          })
          .map(contract => ({
            id: this.getClientIdFromContract(contract),
            name: contract.company_name || contract.host_name || contract.client_company_name || contract.name || 'N/A',
            status: contract.status,
            status_display: contract.status_display,
            created_at: contract.created_at
          }))
          .filter(client => !!client.id)
      }
      if (this.getMyCompany && this.getMyCompany.client_companies) {
        // Fallback for old API response format
        return this.getMyCompany.client_companies
          .map(client => ({
            ...client,
            id: client.id || client.company_id || client.client_company_id
          }))
          .filter(client => !!client.id)
      }
      return []
    }
  },
  methods: {
    getClientIdFromContract(contract) {
      if (!contract) return null
      return contract.company_id || contract.client_company_id || contract.host_company_id || contract.id_company || contract.id || null
    },
    getAssignedCompanyIdFromGuard(guard) {
      if (!guard) return null
      if (guard.assigned_company_id) return guard.assigned_company_id
      if (guard.assigned_company && typeof guard.assigned_company === 'object') {
        return guard.assigned_company.id || guard.assigned_company.company_id || null
      }
      return guard.assigned_company || null
    },
    fetchData() {
      const token = this.getToken
      this.$store.dispatch(`security/${FETCH_MY_COMPANY}`, token)
      this.$store.dispatch(`security/${FETCH_GUARDS}`, token)
    },
    createGuard() {
      if (!this.newGuard.ineFrontFile || !this.newGuard.ineBackFile || !this.newGuard.responsibilityFormatFile) {
        this.$store.commit('snackbar_change_message', 'INE frente, INE reverso y formato de responsabilidad son obligatorios')
        this.$store.commit('snackbar_change_color', 'warning')
        this.$store.commit('snackbar_activate_true')
        this.createGuardTab = 1
        return
      }

      const token = this.getToken
      this.$store.dispatch(`security/${CREATE_GUARD}`, {
        token,
        firstName: this.newGuard.firstName,
        lastName: this.newGuard.lastName,
        password: this.newGuard.password,
        avatar: this.newGuard.avatarFile,
        ine: this.newGuard.ineFrontFile,
        ineReverso: this.newGuard.ineBackFile,
        responsibilityFormat: this.newGuard.responsibilityFormatFile,
        sua: this.newGuard.suaFile
      })
      .then(resp => {
        // Persist plaintext password in the guard object so it can appear in the PDF report
        if (resp && resp.id) {
          this._guardPasswords = this._guardPasswords || {}
          this._guardPasswords[resp.id] = this.newGuard.password
        }
        this.$store.commit('snackbar_change_message', this.$t('security_guard_created_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.showCreateGuardDialog = false
        this.newGuard = {
          firstName: '',
          lastName: '',
          password: '',
          avatarPreview: '',
          avatarFile: null,
          isActive: true,
          ineFrontFile: null,
          ineFrontName: '',
          ineBackFile: null,
          ineBackName: '',
          responsibilityFormatFile: null,
          responsibilityFormatName: '',
          suaFile: null,
          suaName: ''
        }
        this.createGuardTab = 0
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_creating_guard'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
    },
    openEditDialog(guard) {
      this.isEditing = true;
      this.editingGuardId = guard.id;
      this.newGuard = {
        firstName: guard.first_name,
        lastName: guard.last_name,
        password: '',
        avatarPreview: guard.avatar || '',
        avatarFile: null,
        isActive: guard.is_active,
        ineFrontFile: null,
        ineFrontName: '',
        ineBackFile: null,
        ineBackName: '',
        responsibilityFormatFile: null,
        responsibilityFormatName: '',
        suaFile: null,
        suaName: ''
      };
      this.createGuardTab = 0;
      this.showCreateGuardDialog = true;
    },
    saveEditGuard() {
      const token = this.getToken;
      this.$store.dispatch(`security/${UPDATE_GUARD}`, {
        token,
        guardId: this.editingGuardId,
        firstName: this.newGuard.firstName,
        lastName: this.newGuard.lastName,
        password: this.newGuard.password || undefined,
        avatar: this.newGuard.avatarFile || undefined,
        isActive: this.newGuard.isActive,
        ine: this.newGuard.ineFrontFile || undefined,
        sua: this.newGuard.suaFile || undefined,
        responsibilityFormat: this.newGuard.responsibilityFormatFile || undefined
      })
      .then(() => {
        this.$store.commit('snackbar_change_message', this.$t('security_guard_updated_success') || 'Guardia actualizado');
        this.$store.commit('snackbar_change_color', 'success');
        this.$store.commit('snackbar_activate_true');
        this.closeCreateDialog();
        this.fetchData();
      })
      .catch((err) => {
        this.$store.commit('snackbar_change_message', this.$t('error_updating_guard') || 'Error al actualizar guardia');
        this.$store.commit('snackbar_change_color', 'error');
        this.$store.commit('snackbar_activate_true');
      });
    },
    closeCreateDialog() {
      this.showCreateGuardDialog = false;
      this.createGuardTab = 0;
      this.isEditing = false;
      this.editingGuardId = null;
      this.newGuard = {
        firstName: '',
        lastName: '',
        password: '',
        avatarPreview: '',
        avatarFile: null,
        isActive: true,
        ineFrontFile: null,
        ineFrontName: '',
        ineBackFile: null,
        ineBackName: '',
        responsibilityFormatFile: null,
        responsibilityFormatName: '',
        suaFile: null,
        suaName: ''
      };
      if (this.$refs.createGuardForm) {
        this.$refs.createGuardForm.reset();
      }
    },
    openAvatarPicker() {
      this.$refs.avatarInput.value = '';
      this.$refs.avatarInput.click();
    },
    onAvatarFilePicked(event) {
      const files = event.target.files;
      if (files && files[0]) {
        const file = files[0];
        if (file.type.indexOf('image/') !== 0) return;
        this.newGuard.avatarFile = file;
        const reader = new FileReader();
        reader.onload = e => {
          this.newGuard.avatarPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    onDocumentFilePicked(fileKey, nameKey, event) {
      const files = event.target.files
      if (!files || !files[0]) return
      const file = files[0]
      this.newGuard[fileKey] = file
      this.newGuard[nameKey] = file.name
    },
    viewGuardDetail(guard) {
      const token = this.getToken
      this.$store.dispatch(`security/${FETCH_GUARD_DETAIL}`, {
        token,
        guardId: guard.id
      })
      .then(() => {
        this.showGuardDetailDialog = true
      })
    },
    openAssignGuardDialog(guard) {
      this.selectedGuard = guard
      this.selectedAssignClient = this.getAssignedCompanyIdFromGuard(guard)
      this.showAssignGuardDialog = true
    },
    assignGuard() {
      if (!this.selectedGuard || !this.selectedGuard.id) {
        return
      }

      let assignedCompanyId = this.selectedAssignClient
      if (assignedCompanyId === '' || assignedCompanyId === undefined) {
        assignedCompanyId = null
      }

      if (assignedCompanyId !== null && assignedCompanyId !== undefined) {
        const normalizedCompanyId = Number(assignedCompanyId)
        if (!normalizedCompanyId) {
          this.$store.commit('snackbar_change_message', this.$t('security_select_client_required'))
          this.$store.commit('snackbar_change_color', 'warning')
          this.$store.commit('snackbar_activate_true')
          return
        }
        assignedCompanyId = normalizedCompanyId
      }

      const token = this.getToken
      this.$store.dispatch(`security/${ASSIGN_GUARD}`, {
        token,
        guardId: this.selectedGuard.id,
        assignedCompanyId
      })
      .then(resp => {
        this.$store.commit(
          'snackbar_change_message',
          assignedCompanyId === null
            ? 'Guardia desvinculado correctamente'
            : this.$t('security_guard_assigned_success')
        )
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.showAssignGuardDialog = false
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_assigning_guard'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
    },
    openDeleteDialog(guard) {
      this.guardToDelete = guard;
      this.showDeleteGuardDialog = true;
    },
    confirmDeleteGuard() {
      if (!this.guardToDelete) return;

      const token = this.getToken;
      this.$store.dispatch(`security/${DELETE_GUARD}`, {
        token,
        guardId: this.guardToDelete.id
      })
      .then(resp => {
        this.$store.commit('snackbar_change_message', this.$t('security_guard_deleted_success'));
        this.$store.commit('snackbar_change_color', 'success');
        this.$store.commit('snackbar_activate_true');
        this.showDeleteGuardDialog = false;
        this.guardToDelete = null;
        this.fetchData();
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_deleting_guard'));
        this.$store.commit('snackbar_change_color', 'error');
        this.$store.commit('snackbar_activate_true');
        this.showDeleteGuardDialog = false;
      })
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    downloadPDF() {
      const doc = new jsPDF({ orientation: 'landscape' })
      const companyName = (this.getMyCompany && this.getMyCompany.company_name) || 'Empresa de Seguridad'
      const now = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })

      // Header
      doc.setFontSize(16)
      doc.setTextColor(33, 33, 33)
      doc.text('Reporte de Guardias de Seguridad', 14, 18)
      doc.setFontSize(10)
      doc.setTextColor(100)
      doc.text(`Empresa: ${companyName}`, 14, 26)
      doc.text(`Generado el: ${now}`, 14, 32)

      const rows = this.guards.map((g, i) => [
        i + 1,
        `${g.first_name || ''} ${g.last_name || ''}`.trim() || 'N/A',
        g.username || 'N/A',
        g.badge_number || 'N/A',
        g.assigned_company_name || 'Sin asignar',
        g.is_active ? 'Activo' : 'Inactivo',
        g._plainPassword || (this._guardPasswords && this._guardPasswords[g.id]) || '(ver al crear)'
      ])

      doc.autoTable({
        startY: 38,
        head: [['#', 'Nombre', 'Usuario', 'Placa / Gafete', 'Empresa Asignada', 'Estado', 'Contraseña']],
        body: rows,
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [235, 245, 255] },
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
          0: { halign: 'center', cellWidth: 10 },
          5: { halign: 'center', cellWidth: 20 },
          6: { halign: 'center', cellWidth: 30 }
        }
      })

      // Footer note about passwords
      const finalY = doc.lastAutoTable.finalY + 8
      doc.setFontSize(8)
      doc.setTextColor(150)
      doc.text(
        'Nota: Las contraseñas no se almacenan en texto plano. El campo muestra "(ver al crear)" para guardias existentes.',
        14,
        finalY
      )

      doc.save(`guardias_seguridad_${Date.now()}.pdf`)
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }
    if (this.getProfile.roll !== 8) {
      this.$router.push('/')
      return
    }
    this.fetchData()
  }
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 20px;
}
</style>
